import { Method } from '~~/enum'
import { ERROR_HTTP_CODE } from '~~/enum/error'
import {
  API,
  NO_TOKEN,
  INVALID_TOKEN,
  ERROR_CODE_TOAST_EXCLUDE,
  URL_TOAST_EXCLUDE,
} from '~~/config'
import useSystemStore from '~~/store/system'
import useUserStore from '~~/store/user'
import { Encrypt } from '~~/utils/encrypt'
import { isClient } from '~~/utils'
import { FetchOptions } from 'ofetch'
import { RequestLog, ResponseLog, ErrorLog } from '~~/logger'
import { showLoading, hideLoading, showMessage } from '~~/components/toast'

interface IRequestParams<T = IObject> {
  url: string
  method: Method
  data?: T
  isLoading?: boolean
}

type PicKParams<T> = Pick<IRequestParams<T>, 'url' | 'data' | 'isLoading'>

function showSystemBusy() {
  const { $i18n } = useNuxtApp()
  showMessage($i18n.t('system_busy'))
}

export default defineNuxtPlugin((nuxtApp: any) => {
  const config = useRuntimeConfig()
  const fetchIntercept = (isLoading?: boolean) =>
    $fetch.create({
      retry: 0,
      onRequest({ request, options }) {
        if (isLoading) showLoading()
        RequestLog(request, options)
        const systemStore = useSystemStore()
        const userStore = useUserStore()
        const commonHead = {
          brand: '1',
          terminal: 'GW_APP_Haval',
          enterpriseId: 'CC01',
          rs: '2',
          appId: '6',
          'If-Modified-Since': ['0'],
        } as IObject
        // --- 验签 start
        const encrypt = new Encrypt({
          path: request as string,
          method: options?.method?.toUpperCase() ?? '',
          params: options.params,
          body: options.body,
          appkey: config.public.appkey,
          secretkey: config.public.secretkey,
        })
        const sign = encrypt.getHearderSign()
        // --- 验签 end
        options.headers = Object.assign(commonHead || {}, {
          deviceType: '2', // 三端细分类型 PC=0,PAD=1,MOBILE=2
          request_no: 'N' + new Date().getTime(),
          accessToken: userStore.accessToken,
          gwId: userStore.gwId,
          language: systemStore.info.language,
          systemType: systemStore.info.systemType || '1',
          channel: systemStore.channel,
          regionCode: systemStore.info.country,
          country: systemStore.info.country,
          cVer: systemStore.info.appVersion || 'false',
          ...sign,
        })
      },
      onRequestError(error) {
        ErrorLog(error)
        if (isClient()) showSystemBusy()
      },
      async onResponse({ request, options, response }) {
        if (isLoading) hideLoading()
        ResponseLog(request, options, response)
        const userStore = useUserStore()
        const { code = '', description = '' } = response._data
        if (nuxtApp.$bt && [...INVALID_TOKEN, ...NO_TOKEN].includes(code)) {
          if (userStore.accessToken) {
            if (INVALID_TOKEN.includes(code)) {
              nuxtApp.$bt.call.invalidToken({
                code,
                description: description,
              })
            }
          } else {
            nuxtApp.$bt.call.presentLoginController()
          }
        }
        if (
          !ERROR_CODE_TOAST_EXCLUDE.includes(code) &&
          !URL_TOAST_EXCLUDE.find((item) => `${request}`.includes(item))
        ) {
          description && showMessage(description)
        }
      },
      onResponseError(error) {
        if (isLoading) hideLoading()
        console.log('onResponseError', error)
        ErrorLog(error)
        if (isClient()) showSystemBusy()
      },
    })

  function request({ url, method, data, isLoading }: IRequestParams) {
    const option: Pick<FetchOptions, 'params' | 'body'> & {
      method: Method
    } = {
      method,
    }
    option[method === Method.GET ? 'params' : 'body'] = data
    return fetchIntercept(isLoading)(url, { ...option }).catch((error) => {
      console.log('request-error:', error)
      return null
    })
  }

  return {
    provide: {
      request,
    },
  }
})

declare module '#app' {
  interface NuxtApp {
    $request<T, R>(option: IRequestParams<T>): Promise<IResponse<R>>
  }
}

export class Request {
  static get<T, R>({ url, data, isLoading = false }: PicKParams<T>) {
    return this._request<T, R>({ url, data, method: Method.GET, isLoading })
  }

  static post<T, R>({ url, data, isLoading = false }: PicKParams<T>) {
    return this._request<T, R>({
      url,
      data,
      method: Method.POST,
      isLoading,
    })
  }

  static delete<T, R>({ url, data, isLoading = false }: PicKParams<T>) {
    return this._request<T, R>({
      url,
      data,
      method: Method.DELETE,
      isLoading,
    })
  }

  static put<T, R>({ url, data, isLoading = false }: PicKParams<T>) {
    return this._request<T, R>({ url, data, method: Method.PUT, isLoading })
  }

  private static _request<T, R>({ url, data, method, isLoading }: IRequestParams<T>) {
    const { $request, $bt } = useNuxtApp()
    const cacheRequest = () =>
      $request<T, R>({
        url: `${API.PRE}${url}`,
        data,
        method,
        isLoading,
      })

    return new Promise<IResponse<R>>(async (resolve) => {
      try {
        const cache = await cacheRequest()
        const userStore = useUserStore()
        const systemStore = useSystemStore()
        if (isClient()) {
          // 登录状态校验
          if (ERROR_HTTP_CODE.TOKEN_ERROR === cache.code) {
            console.log('刷新Token', $bt.call.refreshToken)
            $bt.call.refreshToken({}, async function (refreshTokenObj: { accessToken: string }) {
              if (refreshTokenObj.accessToken) {
                console.log('新Token ====>', refreshTokenObj.accessToken)
                userStore.SET_TOKEN(refreshTokenObj.accessToken)
                return resolve(await cacheRequest())
              }
              $bt.call.presentLoginController()
            })
            if (!systemStore.isGWM) {
              return resolve(cache)
            }
          } else {
            // 刷新token 我们这边不再处理跳登录问题，app会自动跳转登录
            return resolve(cache)
          }
        }
      } catch (e) {
        console.log(e)
        return resolve({ code: '', data: {} as R })
      }
    })
  }
}
